import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRightLeft,
  Boxes,
  ClipboardCheck,
  Loader2,
  Package,
  PackageX,
  Plus,
  Trash2,
  TriangleAlert,
  Truck,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyState } from '../components/EmptyState';
import { HelpHint } from '../components/HelpHint';
import { Tooltip } from '../components/Hover';
import { Modal } from '../components/Modal';
import { PageNav } from '../components/PageNav';
import { ReorderLevels } from '../components/ReorderLevels';
import { api } from '../lib/api';
import { useSession } from '../lib/session';

const HEALTH_TONE: Record<string, string> = {
  ok: '',
  low: 'text-amber-700',
  out: 'text-danger-700',
};

const KIND_ICON: Record<string, typeof Package> = {
  store: Boxes,
  van: Truck,
  site: Package,
  scrap: PackageX,
};

/**
 * Where the stock is.
 *
 * ## Vans are locations, and that is the whole idea
 *
 * A retail system models branches. An ISP's real stock problem is the fourteen
 * ONTs in a technician's boot that nobody has counted since March — so every
 * technician gets a location, issuing to them is a transfer, and the materials
 * they record on a job come off *their* van. The balance shown against a van is
 * therefore a number somebody can be asked about.
 *
 * A van can show a negative figure. That is not a bug and it is not hidden:
 * it means stock reached that technician without being issued through the
 * system, which is exactly the kind of gap this page exists to make visible.
 */
export function StockPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [moving, setMoving] = useState<{ kind: string; locationId: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const overview = useQuery({ queryKey: ['stock-overview'], queryFn: () => api.stockOverview() });
  const reorder = useQuery({
    queryKey: ['stock-reorder'],
    queryFn: () => api.stockToReorder(),
    enabled: can('stock:read'),
  });

  const ensure = useMutation({
    mutationFn: () => api.ensureStockLocations(),
    onSuccess: async (result) => {
      setNotice(result.message);
      await queryClient.invalidateQueries({ queryKey: ['stock-overview'] });
    },
  });

  const locations = overview.data?.locations ?? [];
  const store = locations.find((location) => location.kind === 'store');
  const toOrder = reorder.data?.rows ?? [];

  return (
    <div className="space-y-6">
      <PageNav crumbs={[{ label: 'Stock' }]} />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-1.5 font-heading text-2xl">
            Stock
            <HelpHint>
              Every balance here is added up from the movements behind it, never stored as a
              number somebody can edit. That is why each figure can be traced to who moved it and
              when.
            </HelpHint>
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            {overview.data
              ? `${overview.data.totalValue} across ${locations.length} location${locations.length === 1 ? '' : 's'}.`
              : 'The store, and every technician’s van.'}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {can('stock:manage') && (
            <button
              type="button"
              className="btn-quiet"
              disabled={ensure.isPending}
              onClick={() => ensure.mutate()}
            >
              {ensure.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Truck className="h-4 w-4" aria-hidden="true" />
              )}
              Set up the vans
            </button>
          )}
          {can('stock:move') && store && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setMoving({ kind: 'issue', locationId: store.id })}
            >
              <ArrowRightLeft className="h-4 w-4" aria-hidden="true" />
              Move stock
            </button>
          )}
        </div>
      </div>

      {notice && (
        <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </p>
      )}

      {overview.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Adding up the ledger
        </p>
      )}

      {overview.data && locations.length === 0 && (
        <EmptyState
          icon={Boxes}
          headline="No stock locations yet"
          body="Set up the store and a van for each technician, then book in your first delivery against a purchase order."
          {...(can('stock:manage')
            ? { secondary: { label: 'Set up the vans', onClick: () => ensure.mutate() } }
            : {})}
        />
      )}

      {/* ── What to order ─────────────────────────────────────────────────── */}
      {toOrder.length > 0 && (
        <div className="card border-amber-200 bg-amber-50/50">
          <h2 className="flex items-center gap-2 font-heading text-lg">
            <TriangleAlert className="h-4 w-4 text-amber-700" aria-hidden="true" />
            Worth ordering
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            In the store only — a van running low is a request to the store, not an order to a
            supplier.
          </p>
          <ul className="mt-3 space-y-1.5 text-sm">
            {toOrder.map((row) => (
              <li key={row.code} className="flex flex-wrap items-baseline gap-2">
                <span className={`font-medium ${HEALTH_TONE[row.health]}`}>{row.name}</span>
                <span className="text-muted-foreground">
                  {row.health === 'out' ? 'none left' : `${row.have} left`}, reorder level{' '}
                  {row.reorderLevel}
                </span>
                <span className="ml-auto tabular-nums">order {row.order}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── By location ───────────────────────────────────────────────────── */}
      <div className="space-y-4">
        {locations.map((location) => {
          const Icon = KIND_ICON[location.kind] ?? Package;
          const negatives = location.items.filter((item) => item.quantity < 0);

          return (
            <div key={location.id} className="card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="flex items-center gap-2 font-heading text-lg">
                  <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  {location.name}
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm tabular-nums text-muted-foreground">
                    {location.value}
                  </span>
                  {can('stock:count') && (
                    <Tooltip label="Count what is actually there and correct the ledger" side="left">
                      <StartCountButton locationId={location.id} />
                    </Tooltip>
                  )}
                </div>
              </div>

              {negatives.length > 0 && (
                <p className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {negatives.length} item{negatives.length === 1 ? '' : 's'} showing below zero —
                  stock reached here without being issued through the system. A stock count will
                  settle it.
                </p>
              )}

              {location.items.length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">Nothing here.</p>
              ) : (
                <ul className="mt-3 grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2 lg:grid-cols-3">
                  {location.items.map((item) => (
                    <li key={item.code} className="flex items-baseline justify-between gap-3">
                      <span className={HEALTH_TONE[item.health]}>{item.name}</span>
                      <span
                        className={`tabular-nums ${item.quantity < 0 ? 'font-medium text-danger-700' : 'text-muted-foreground'}`}
                      >
                        {item.display}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/*
        Configuration, and only for whoever holds `stock:manage` — which is an
        admin and nobody else. A dispatcher can see every balance on this page
        and cannot change what "low" means, because that is a standing decision
        about the business rather than part of running today.
      */}
      {can('stock:manage') && <ReorderLevels />}

      {moving && (
        <MoveStockDialog
          initial={moving}
          onClose={() => setMoving(null)}
          onDone={async (message) => {
            setNotice(message);
            setMoving(null);
            await queryClient.invalidateQueries({ queryKey: ['stock-overview'] });
            await queryClient.invalidateQueries({ queryKey: ['stock-reorder'] });
          }}
        />
      )}
    </div>
  );
}

function StartCountButton({ locationId }: { locationId: string }) {
  const navigate = useNavigate();
  const [result, setResult] = useState<string | null>(null);

  const open = useMutation({
    mutationFn: () => api.openStockCount(locationId),
    // `navigate`, not `window.location`. Assigning to the latter reloads the
    // whole application, which on a technician's phone means downloading the
    // bundle again while standing in a stockroom.
    onSuccess: (data) => navigate(`/stock/counts/${data.id}`),
    onError: (e: Error) => setResult(e.message),
  });

  return (
    <>
      <button
        type="button"
        className="btn-quiet text-xs"
        disabled={open.isPending}
        onClick={() => open.mutate()}
      >
        {open.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        ) : (
          <ClipboardCheck className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        Count it
      </button>
      {result && <span className="text-xs text-danger-700">{result}</span>}
    </>
  );
}

type DraftLine = { code: string; quantity: string; serial: string };

/**
 * Issue, receive back, or write off.
 *
 * The serial box appears only for items that need one, and only on the kinds
 * where it can be known. Asking a storeman to type ten serial numbers to move
 * ten routers from the store to a van — serials that were already captured when
 * those routers were booked in — is how a form stops being used.
 */
function MoveStockDialog({
  initial,
  onClose,
  onDone,
}: {
  initial: { kind: string; locationId: string };
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [kind, setKind] = useState(initial.kind);
  const [locationId, setLocationId] = useState(initial.locationId);
  const [counterpartyLocationId, setCounterparty] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([{ code: '', quantity: '', serial: '' }]);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const locations = useQuery({ queryKey: ['stock-locations'], queryFn: () => api.stockLocations() });
  const catalogue = useQuery({ queryKey: ['stock-catalogue'], queryFn: () => api.stockCatalogue() });

  const specs = new Map((catalogue.data ?? []).map((item) => [item.code, item]));
  const isTransfer = kind === 'issue' || kind === 'returned';

  const submit = useMutation({
    mutationFn: () =>
      api.moveStock({
        kind,
        locationId,
        counterpartyLocationId: isTransfer ? counterpartyLocationId || undefined : undefined,
        note: note.trim() || undefined,
        lines: lines
          .filter((line) => line.code && line.quantity)
          .map((line) => ({
            code: line.code,
            quantity: Number(line.quantity),
            serial: line.serial.trim() || undefined,
          })),
      }),
    onSuccess: (result) => onDone(result.message),
    onError: (e: Error) => setError(e.message),
  });

  const setLine = (index: number, patch: Partial<DraftLine>) =>
    setLines((current) =>
      current.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    );

  // Through `Modal`, which portals out of main's stacking context.
  return (
    <Modal title="Move stock" onClose={onClose} wide>
      <>

        <div className="mt-4 flex flex-wrap gap-3">
          <label className="text-sm">
            What is happening
            <select
              className="input mt-1"
              value={kind}
              onChange={(event) => setKind(event.target.value)}
            >
              <option value="issue">Issue to a van</option>
              <option value="returned">Return to the store</option>
              <option value="write_off">Write off</option>
            </select>
          </label>

          <label className="text-sm">
            {kind === 'issue' ? 'Out of' : 'Into'}
            <select
              className="input mt-1"
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
            >
              {(locations.data ?? []).map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>

          {isTransfer && (
            <label className="text-sm">
              {kind === 'issue' ? 'Into' : 'Out of'}
              <select
                className="input mt-1"
                value={counterpartyLocationId}
                onChange={(event) => setCounterparty(event.target.value)}
              >
                <option value="">Choose…</option>
                {(locations.data ?? [])
                  .filter((location) => location.id !== locationId)
                  .map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
              </select>
            </label>
          )}
        </div>

        <div className="mt-4 space-y-2">
          {lines.map((line, index) => {
            const spec = specs.get(line.code);
            return (
              <div key={index} className="flex flex-wrap items-end gap-2">
                <label className="min-w-48 flex-1 text-sm">
                  {index === 0 && 'Item'}
                  <select
                    className="input mt-1"
                    value={line.code}
                    onChange={(event) => setLine(index, { code: event.target.value })}
                  >
                    <option value="">Choose an item…</option>
                    {(catalogue.data ?? []).map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.name}
                        {item.aka ? ` (${item.aka})` : ''}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="w-28 text-sm">
                  {index === 0 && (spec?.unit === 'metre' ? 'Metres' : 'How many')}
                  <input
                    className="input mt-1 tabular-nums"
                    inputMode="decimal"
                    value={line.quantity}
                    onChange={(event) => setLine(index, { quantity: event.target.value })}
                  />
                </label>

                {spec?.requiresSerial && kind === 'write_off' && (
                  <label className="min-w-40 flex-1 text-sm">
                    {index === 0 && 'Serial'}
                    <input
                      className="input mt-1"
                      value={line.serial}
                      onChange={(event) => setLine(index, { serial: event.target.value })}
                      placeholder="Comma separated"
                    />
                  </label>
                )}

                {lines.length > 1 && (
                  <button
                    type="button"
                    className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
                    onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                    <span className="sr-only">Remove this line</span>
                  </button>
                )}
              </div>
            );
          })}

          <button
            type="button"
            className="btn-quiet text-xs"
            onClick={() => setLines((current) => [...current, { code: '', quantity: '', serial: '' }])}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Another item
          </button>
        </div>

        <label className="mt-4 block text-sm">
          Note <span className="text-muted-foreground">(optional)</span>
          <input
            className="input mt-1"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={kind === 'write_off' ? 'Damaged in transit' : 'Monday loadout'}
          />
        </label>

        {error && (
          <p className="mt-4 whitespace-pre-line rounded-xl border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
            {error}
          </p>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" className="btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={
              submit.isPending ||
              (isTransfer && !counterpartyLocationId) ||
              !lines.some((line) => line.code && line.quantity)
            }
            onClick={() => {
              setError(null);
              submit.mutate();
            }}
          >
            {submit.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <ArrowRightLeft className="h-4 w-4" aria-hidden="true" />
            )}
            Record it
          </button>
        </div>
      </>
    </Modal>
  );
}
